import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { TourObj } from "../../../shared/types";
import TourCard from "./TourCard";
import FilterBar, { SortKey } from "./FilterBar";
import Pagination from "./Pagination";
import { useGetToursQuery } from "../toursApiSlice";
import LoadingPage from "../../../shared/LoadingPage";
import Reveal from "../../../shared/design/Reveal";

const PageSize = 9;

const AllTours = () => {
  const { data, isLoading, isSuccess, isError } = useGetToursQuery("");

  const [q, setQ] = useState("");
  const [difficulty, setDifficulty] = useState("All");
  const [sort, setSort] = useState<SortKey>("featured");
  const [currentPage, setCurrentPage] = useState(1);

  const tours: TourObj[] = useMemo(
    () => (isSuccess && data ? data.data.data : []),
    [isSuccess, data]
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    let result = tours.filter((t) => {
      if (difficulty !== "All" && t.difficulty !== difficulty.toLowerCase())
        return false;
      if (
        query &&
        !`${t.name} ${t.startLocation?.description ?? ""} ${t.summary ?? ""}`
          .toLowerCase()
          .includes(query)
      )
        return false;
      return true;
    });
    // "featured" keeps the API order; the rest sort a copy.
    if (sort === "price-asc") result = [...result].sort((a, b) => a.price - b.price);
    else if (sort === "price-desc")
      result = [...result].sort((a, b) => b.price - a.price);
    else if (sort === "rating")
      result = [...result].sort((a, b) => b.ratingsAverage - a.ratingsAverage);
    else if (sort === "duration")
      result = [...result].sort((a, b) => a.duration - b.duration);
    return result;
  }, [tours, q, difficulty, sort]);

  // Reset to first page whenever the result set changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [q, difficulty, sort]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  const pageTours = useMemo(() => {
    const first = (currentPage - 1) * PageSize;
    return filtered.slice(first, first + PageSize);
  }, [filtered, currentPage]);

  const clearAll = () => {
    setQ("");
    setDifficulty("All");
    setSort("featured");
  };

  return (
    <div className="min-h-screen">
      {/* Page head */}
      <section className="mx-auto max-w-[1320px] px-6 pb-5 pt-24 md:px-12 md:pt-28">
        <NavLink
          to="/"
          className="wn-link mb-4 inline-block font-mono text-[12.5px] tracking-[0.08em] text-secondary-400"
        >
          ← HOME
        </NavLink>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="mb-2.5 font-serif text-[40px] font-normal tracking-[-0.02em] md:text-[56px]">
              The collection
            </h1>
            <p className="max-w-[520px] text-[17px] text-muted-200">
              Guided expeditions across North America. Filter to find the one
              with your name on it.
            </p>
          </div>
          <div className="font-mono text-[13px] tracking-[0.04em] text-muted-300">
            SHOWING <span className="font-medium text-ink">{filtered.length}</span> /{" "}
            {tours.length} TOURS
          </div>
        </div>
      </section>

      <FilterBar
        q={q}
        setQ={setQ}
        difficulty={difficulty}
        setDifficulty={setDifficulty}
        sort={sort}
        setSort={setSort}
      />

      {/* Grid / states */}
      <section className="mx-auto max-w-[1320px] px-6 pb-5 pt-8 md:px-12">
        {isLoading ? (
          <LoadingPage />
        ) : isError ? (
          <p className="py-20 text-center text-hard">
            Unable to fetch tour data. Please refresh, and contact us if the
            problem persists.
          </p>
        ) : filtered.length ? (
          <div className="grid grid-cols-1 gap-[26px] md:grid-cols-2 lg:grid-cols-3">
            {pageTours.map((tour, i) => (
              <Reveal key={tour.id} delay={Math.min(i * 0.04, 0.24)}>
                <TourCard tourObj={tour} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="px-5 py-20 text-center text-muted-300">
            <div className="mb-2 font-serif text-[30px] text-ink">
              No trips match — yet.
            </div>
            <div className="text-[15px]">
              Try clearing a filter or widening your search.
            </div>
            <button
              onClick={clearAll}
              className="mt-5 rounded-full bg-secondary-400 px-6 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-ink"
            >
              Clear all filters
            </button>
          </div>
        )}
      </section>

      <Pagination
        currentPage={currentPage}
        totalCount={filtered.length}
        pageSize={PageSize}
        onPageChange={(page) => setCurrentPage(page as number)}
      />
    </div>
  );
};

export default AllTours;
